import { useAuth } from 'hooks/useAuth';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';

// State pentru gestionarea refresh-ului
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

// Funcție pentru a adăuga request-uri în coadă
const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

// Funcție pentru a notifica toate request-urile din coadă
const onRefreshed = (token: string) => {
  refreshSubscribers.map(cb => cb(token));
  refreshSubscribers = [];
};

// Funcție pentru a curăța coada în caz de eroare
const onRefreshError = () => {
  refreshSubscribers = [];
};

const http = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

http.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Dacă se face deja refresh, adaugă request-ul în coadă
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(http(originalRequest));
          });
        });
      }

      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        useAuth().logout();
        return Promise.reject(error);
      }

      // Marchează că se face refresh
      isRefreshing = true;

      try {
        let fingerprintBody = {};
        try {
          const raw = localStorage.getItem('fpjs');
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.visitorId) {
              fingerprintBody = {
                fingerprintVisitorId: parsed.visitorId,
                fingerprintConfidence: parsed.fingerprintConfidence,
              };
            }
          }
        } catch {
          /* ignore */
        }

        const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/auth/refresh`, fingerprintBody, {
          headers: { Authorization: `Bearer ${refreshToken}` }
        });

        const newAccessToken = data.data.accessToken;
        const newRefreshToken = data.data.refreshToken;

        // Actualizează token-urile
        localStorage.setItem('accessToken', newAccessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        if (data.data.sessionId) localStorage.setItem('sessionId', data.data.sessionId);
        
        // Notifică toate request-urile din coadă
        onRefreshed(newAccessToken);
        
        // Actualizează request-ul original
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }
        
        return http(originalRequest);
      } catch (refreshError) {
        // Curăță coada și fă logout
        console.log('Refresh token error:', refreshError);
        onRefreshError();
        useAuth().logout();
        return Promise.reject(refreshError);
      } finally {
        // Resetează flag-ul
        isRefreshing = false;
      }
    }

    if (error.response?.status === 423) {
      // Dacă este un 423, poate fi o problemă de permisiuni
      useAuth().logout();
    }

    return Promise.reject(error);
  }
);

export default http;